// ============================================================================
// "Checked with an account manager" — Ben, 2026-09-23: *"which AM reviewed and
// approved this? … so that Bryan can see 'did the Sales person check the bid
// with an AM who may pick up on small things that would change the bid?'"*
// The ONE component that writes the review (§15a). Who counts is the database's
// rule (bid_reviewers(): an account manager or the national AM, login active) —
// the guard refuses anyone else, and the list here is that same rule.
// ============================================================================

import { useEffect, useState } from 'react';
import { Button } from '../../shared/components';
import { fetchReviewers, updateBid, type BidRow, type Reviewer } from './api';
import { Field, INPUT, SaveError } from './fields';
import { saveText } from './start-form';
import { shortDate } from './format';
import { useBidLoad } from './use-bid-load';

function firstName(r: Reviewer | undefined): string {
  return (r?.display_name ?? '').split(' ')[0] || 'they';
}

export function AmReviewCard({ bid, locked }: { bid: BidRow; locked?: boolean }) {
  const { data: reviewers } = useBidLoad<Reviewer[]>(fetchReviewers, [], []);
  const [editing, setEditing] = useState(!bid.am_reviewed_by_eid);
  const [who, setWho] = useState(bid.am_reviewed_by_eid ?? '');
  const [when, setWhen] = useState('');
  const [notes, setNotes] = useState(bid.am_review_notes ?? '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setWho(bid.am_reviewed_by_eid ?? '');
    setNotes(bid.am_review_notes ?? '');
    setWhen((bid.am_reviewed_at ?? new Date().toISOString()).slice(0, 10));
    setEditing(!bid.am_reviewed_by_eid);
  }, [bid.bid_id, bid.am_reviewed_by_eid, bid.am_review_notes, bid.am_reviewed_at]);

  const chosen = reviewers.find((r) => r.employee_id === who);
  const saved = reviewers.find((r) => r.employee_id === bid.am_reviewed_by_eid);
  const ready = !!who && notes.trim().length > 0 && !!when;

  async function save() {
    setSaving(true); setErr(null);
    try {
      await updateBid(bid.bid_id, {
        am_reviewed_by_eid: who,
        am_review_notes: notes.trim(),
        am_reviewed_at: new Date(`${when}T12:00:00`).toISOString(),
      }, 'save the account manager review');
      setEditing(false);
    } catch (e) { setErr(saveText(e)); } finally { setSaving(false); }
  }

  const done = !!bid.am_reviewed_by_eid;
  return (
    <section className={`rounded-xl border bg-tcs-bg p-4 sm:p-5 ${done ? 'border-st-ok-border' : 'border-st-warn-border'}`}>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold text-tcs-text">Checked with an account manager</h2>
        <span className={`rounded-full px-2 py-0.5 text-xs ${done ? 'bg-st-ok-bg text-st-ok-fg' : 'bg-st-warn-bg text-st-warn-fg'}`}>
          {done ? `done · ${shortDate(bid.am_reviewed_at)}` : 'not yet'}
        </span>
      </div>
      <SaveError text={err} />
      {!editing && done ? (
        <div className="space-y-1 text-sm text-tcs-text">
          <div><span className="text-tcs-text-muted">Reviewed by </span>{saved?.display_name ?? bid.am_reviewed_by_eid}{saved?.markets ? ` — ${saved.markets}` : ''}, {shortDate(bid.am_reviewed_at)}</div>
          <div><span className="text-tcs-text-muted">What {firstName(saved)} caught: </span>{bid.am_review_notes}</div>
          {!locked && <div className="pt-2"><Button size="sm" variant="secondary" onClick={() => setEditing(true)}>Change</Button></div>}
        </div>
      ) : locked ? (
        <p className="text-sm text-tcs-text-muted">No account manager review was recorded.</p>
      ) : (
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Which account manager went over this bid?">
              <select className={INPUT} value={who} onChange={(e) => setWho(e.target.value)}>
                <option value="">— pick —</option>
                {reviewers.map((r) => (
                  <option key={r.employee_id} value={r.employee_id}>
                    {r.display_name}{r.markets ? ` — ${r.markets}` : r.title ? ` — ${r.title}` : ''}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Reviewed on">
              <input className={INPUT} type="date" value={when} onChange={(e) => setWhen(e.target.value)} />
            </Field>
          </div>
          <Field label={`What ${chosen ? firstName(chosen) : 'they'} caught`} hint={'"Nothing, the walkthrough matched the plans" is a valid answer.'}>
            <textarea className={INPUT} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
          <div className="flex flex-wrap items-center justify-end gap-3">
            {!ready && <span className="text-xs text-tcs-text-muted">Pick the account manager, the date, and say what they caught.</span>}
            {done && <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>Cancel</Button>}
            <Button onClick={save} disabled={!ready} loading={saving}>Save the review</Button>
          </div>
        </div>
      )}
    </section>
  );
}
